// =============================================================================
// MessageBubble/MessageBubble.tsx — Dispatch polimórfico por tipo de mensagem.
//
// Recebe um `Message` e renderiza o sub-componente correto.
// Alinhamento: inbound (esquerda) vs outbound (direita).
//
// Tipos cobertos:
//   text          → TextBubble
//   image/video/audio/voice/document → MediaBubble
//   template      → TemplateBubble
//   interactive   → InteractiveBubble
//   sticker       → StickerBubble
//   location      → LocationBubble
//   contact       → ContactBubble
//   system        → SystemBubble (centralizada, sem bolha)
//   reaction/story_mention/story_reply/comment/comment_reply/ig_postback/referral/share
//               → ReadOnlyBubble
//   fallback      → UnknownBubble
//
// LGPD (doc 17 §8.1): não loga message.content.
//
// F46-S11 ("Anexar aos documentos", docs/planejamento-documentos-anexos.md
// §6.1): o gate (flag + permissão), o leadId da conversa e o estado do
// diálogo são resolvidos AQUI — uma vez por mensagem, no dispatcher que o
// painel já monta uma vez por mensagem — e repassados por prop a
// `MediaBubble`. Nunca montar esses hooks dentro das variantes de bolha
// (nota do slot: precedente do contador duplicado por hook de socket
// montado duas vezes, useConversationSocket.ts). `ConversationPanel.tsx`
// está fora de `files_allowed` deste slot — por isso o ponto de montagem
// único possível é este dispatcher, não o painel propriamente dito.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../../lib/cn';
import { useDocumentsGate } from '../../hooks/useAttachMediaAsDocument';
import { useConversation } from '../../queries';
import type { Message } from '../../types';
import { AttachToDocumentsDialog } from '../AttachToDocumentsDialog';

import { getAttachedDocumentId, getCanAttachAsDocument } from './attachedDocument';
import { ContactBubble } from './bubbles/ContactBubble';
import { InteractiveBubble } from './bubbles/InteractiveBubble';
import { LocationBubble } from './bubbles/LocationBubble';
import { MediaBubble } from './bubbles/MediaBubble';
import type { MediaAttachState } from './bubbles/MediaBubble';
import { ReadOnlyBubble } from './bubbles/ReadOnlyBubble';
import { StickerBubble } from './bubbles/StickerBubble';
import { SystemBubble } from './bubbles/SystemBubble';
import { TemplateBubble } from './bubbles/TemplateBubble';
import { TextBubble } from './bubbles/TextBubble';
import { UnknownBubble } from './bubbles/UnknownBubble';
import { fileNameFromUrl } from './utils';

// ─── Tipos de agrupamento ────────────────────────────────────────────────────

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'voice', 'document'] as const);
const READONLY_TYPES = new Set([
  'story_mention',
  'story_reply',
  'comment',
  'comment_reply',
  'ig_postback',
  'referral',
  'share',
  'reaction',
] as const);

function isMediaType(type: Message['type']): boolean {
  return MEDIA_TYPES.has(type as 'image' | 'video' | 'audio' | 'voice' | 'document');
}

// ─── Componente ──────────────────────────────────────────────────────────────

export interface MessageBubbleProps {
  message: Message;
  /**
   * Inicial do nome do contato para o avatar inbound.
   * Passado pelo ConversationPanel a partir de `useConversation`.
   * Quando não fornecido, usa '?' como fallback.
   */
  contactInitial?: string | undefined;
}

/**
 * MessageBubble — wrapper de alinhamento + dispatch por tipo.
 *
 * O container flex (`items-start` / `items-end`) posiciona a bolha
 * à esquerda (inbound) ou à direita (outbound).
 * A bolha interna (`self-start` / `self-end`) define sua largura máxima.
 */
export function MessageBubble({
  message,
  contactInitial = '?',
}: MessageBubbleProps): React.JSX.Element {
  const isOutbound = message.direction === 'out';

  // ── F46-S11: gate + leadId + estado do diálogo ──────────────────────────
  //
  // `useConversation` já está em cache (o mesmo `conversationId` é buscado
  // por ConversationPanel/ConversationHeader) — chamar de novo aqui não gera
  // fetch extra, só lê o cache do TanStack Query. Chamado incondicionalmente
  // (regra dos hooks) mesmo para mensagens não-mídia — custo é uma leitura
  // de cache, não uma rede.
  const { data: convDetail } = useConversation(message.conversationId);
  const documentsGate = useDocumentsGate();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const leadId = convDetail?.data.leadId ?? null;
  const attachedDocumentId = getAttachedDocumentId(message);
  const canAttachAsDocument = getCanAttachAsDocument(message);

  const attachState: MediaAttachState = {
    isLoading: documentsGate.isLoading,
    canAttach: documentsGate.canWrite,
    leadId,
    attachedDocumentId,
    canAttachAsDocument,
    onOpenDialog: () => {
      if (leadId) setDialogOpen(true);
    },
  };

  const handleAttached = React.useCallback(() => {
    setDialogOpen(false);
  }, []);

  // ── Sistema: sem bolha, centralizado ──────────────────────────────────────
  if (message.type === 'system') {
    return (
      <div className="flex justify-center px-4 py-0.5" role="status">
        <SystemBubble message={message} />
      </div>
    );
  }

  return (
    <div
      className={cn('flex w-full px-4 py-0.5', isOutbound ? 'justify-end' : 'justify-start')}
      // Não usar aria-label com conteúdo da mensagem (LGPD)
    >
      {/* Avatar inbound (apenas para inbound, lado esquerdo) */}
      {!isOutbound && (
        <div
          className="w-7 h-7 rounded-pill flex items-center justify-center shrink-0 mr-2 mt-1 text-white text-xs font-semibold"
          style={{ background: 'var(--grad-azul)', boxShadow: 'var(--elev-1)' }}
          aria-hidden="true"
        >
          {contactInitial}
        </div>
      )}

      {/* Bolha — o max-width vive AQUI (no wrapper), não no bubble.
          O wrapper é flex item da linha (w-full, largura definida), então `75%`
          resolve contra a linha. Se o max-w ficasse no bubble, o pai shrink-to-fit
          criava restrição circular (largura = 75%×largura) → colapso → frases
          curtas quebravam palavra-a-palavra. */}
      <div
        className={cn(
          'flex flex-col min-w-0 max-w-[min(75%,40rem)]',
          isOutbound ? 'items-end' : 'items-start',
        )}
      >
        {renderBubble(message, isOutbound, attachState)}
      </div>

      {isMediaType(message.type) && leadId && (
        <AttachToDocumentsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          conversationId={message.conversationId}
          leadId={leadId}
          messageId={message.id}
          defaultDisplayName={fileNameFromUrl(message.mediaUrl)}
          onAttached={handleAttached}
        />
      )}
    </div>
  );
}

// ─── Dispatch ────────────────────────────────────────────────────────────────

function renderBubble(
  message: Message,
  isOutbound: boolean,
  attachState: MediaAttachState,
): React.ReactNode {
  const { type } = message;

  if (type === 'text') {
    return <TextBubble message={message} isOutbound={isOutbound} />;
  }

  if (isMediaType(type)) {
    return <MediaBubble message={message} isOutbound={isOutbound} attach={attachState} />;
  }

  if (type === 'template') {
    return <TemplateBubble message={message} isOutbound={isOutbound} />;
  }

  if (type === 'interactive') {
    return <InteractiveBubble message={message} isOutbound={isOutbound} />;
  }

  if (type === 'sticker') {
    return <StickerBubble message={message} isOutbound={isOutbound} />;
  }

  if (type === 'location') {
    return <LocationBubble message={message} isOutbound={isOutbound} />;
  }

  if (type === 'contact') {
    return <ContactBubble message={message} isOutbound={isOutbound} />;
  }

  if (
    READONLY_TYPES.has(
      type as
        | 'story_mention'
        | 'story_reply'
        | 'comment'
        | 'comment_reply'
        | 'ig_postback'
        | 'referral'
        | 'share'
        | 'reaction',
    )
  ) {
    return <ReadOnlyBubble message={message} isOutbound={isOutbound} />;
  }

  // Tipo não reconhecido — graceful degradation
  return <UnknownBubble message={message} isOutbound={isOutbound} />;
}
